import { useState } from 'react';
import { Button, Modal, Spin } from 'antd';
import { Link } from 'react-router';
import type { TemplateDayDto, TemplateDecisionRowDto } from '@pams/shared';
import { TEMPLATE_DECISION_CLASS_QUESTION } from '@/lib/labels';
import { templateDecisionHref } from '@/lib/templateLinks';
import { DECISIONS_COPY, DECISION_MODAL_COPY } from '../copy';
import { choiceWords } from '../Decisions/choiceWords';
import { DecisionContext } from '../Decisions/DecisionContext';
import { isOneTemplateClass } from '../Decisions/decisionContext';
import { DecisionForm, type DecisionFormProps } from '../Decisions/DecisionForm';
import { createPostTypeHref } from '../Decisions/decisionRules';
import { useDecisionMasters } from '../Decisions/useDecisionMasters';
import { useSaveDecision } from '../Decisions/useSaveDecision';
import { useTemplateDecisions } from '../templateQueries';
import { modalLeadOf, type DecisionModalTarget } from './decisionModal';

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  [P7C §4] DECIDE IN PLACE — the library's preview of one open decision, in a dialog, without leaving
 *  the template a person is looking at.
 *
 *  ⚠️ IT IS THE DECISIONS PAGE'S OWN FORM AND THE DECISIONS PAGE'S OWN SAVE. `DecisionForm` renders
 *  unchanged (it parses every body with the shared schema before Save is enabled) and `useSaveDecision`
 *  is the page's mutation moved, not copied — one PUT, the toast, close, one invalidation of the set.
 *  Nothing here decides anything the page could not.
 *
 *  ⚠️ WHAT IT LEADS WITH IS `modalLeadOf`'s: a column whose words describe an existing post type asks
 *  "Looks like X — use it?" first (the server's `suggestion`, the same one the decisions page offers
 *  as "Use the suggestion" — one row, one answer); a column with none offers a super admin the post
 *  type master, the decisions page's own door, which comes back to that column on the decisions page.
 *  "Choose another answer" is always one click from the form.
 *
 *  ⚠️ IT LOADS NOTHING UNTIL IT IS OPENED, AND THEN ONLY WHAT ITS CLASS NEEDS — the class's decision list
 *  and that class's masters, on the decisions page's own cache keys. The library's request census does
 *  not move. A controlled `Modal`, `destroyOnHidden`, swept by `e2e/fixtures/overlays.ts`.
 *
 *  ⚠️ [amended P7C.1 §2] IT OPENS FROM INSIDE THE PANEL'S OWN DIALOG NOW, so two are open: its portal is appended
 *  after the panel's at the same z-index, so it lands on top, and rc-dialog handles Escape on its own wrap, so
 *  Escape closes this one and leaves the panel. Its state lives OUTSIDE the panel's destroyable body.
 *
 *  ⚠️ [amended TEMPLATE-FIND] A PRINTED TOTAL OR A DUTY POINT SHOWS THE DECISIONS PAGE'S OWN CONTEXT HERE
 *  (`DecisionContext`) in place of "Used by 1 template" — where it is printed, what PAMS could not read, and
 *  "Show it in template t152". That link moves the PATHNAME (`/templates/<set>?t=…`, then the library's own
 *  redirect to the template), so the panel's `closeOn` closes BOTH dialogs and the pane opens with the row or
 *  the check highlighted. It loads nothing: the class's list is the one already read, and the number is
 *  resolved from the list the library holds. Every other class keeps its "Used by" line, unlinked.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */
type Masters = ReturnType<typeof useDecisionMasters>;

const ModalBody = ({
  setKey,
  target,
  row,
  masters,
  days,
  mayCreatePostType,
  saving,
  onSave,
  onClose,
}: {
  setKey: string;
  target: DecisionModalTarget;
  row: TemplateDecisionRowDto | undefined;
  masters: Masters;
  days: readonly TemplateDayDto[];
  mayCreatePostType: boolean;
  saving: boolean;
  onSave: DecisionFormProps['onSave'];
  onClose: () => void;
}): React.ReactElement => {
  /* Reset with the dialog: `destroyOnHidden` unmounts this body when it closes. */
  const [another, setAnother] = useState(false);

  if (!row) {
    return (
      <div className="flex flex-col gap-2" data-testid="decision-modal-gone">
        <p>{DECISION_MODAL_COPY.gone}</p>
        <Link to={templateDecisionHref(setKey, target.class)} className="text-link">
          {DECISION_MODAL_COPY.openPage}
        </Link>
      </div>
    );
  }

  const lead = another ? ({ kind: 'FORM' } as const) : modalLeadOf(row, mayCreatePostType, masters);
  const other = (
    <Button
      data-testid="decision-modal-other"
      onClick={() => {
        setAnother(true);
      }}
    >
      {DECISION_MODAL_COPY.otherAnswer}
    </Button>
  );

  return (
    <>
      {isOneTemplateClass(row.class) ? (
        /* [TEMPLATE-FIND] Where it is printed and what to check, then its one link — see the header. */
        <DecisionContext row={row} setKey={setKey} />
      ) : (
        <p className="text-muted-foreground" data-testid="decision-modal-used-by">
          {DECISIONS_COPY.usedBy(row.usedBy)}
        </p>
      )}
      <p>{TEMPLATE_DECISION_CLASS_QUESTION[row.class]}</p>
      {lead.kind !== 'LIKELY' && row.suggestion !== null ? (
        <p data-testid="decision-modal-suggestion">
          <span className="text-muted-foreground">{DECISIONS_COPY.suggestion}: </span>
          {choiceWords(row.class, row.suggestion, row.context, masters.lookups)}
        </p>
      ) : null}

      {lead.kind === 'LIKELY' ? (
        <div className="flex flex-col gap-2">
          <p className="font-medium text-heading" data-testid="decision-modal-likely">
            {lead.vehicle
              ? DECISION_MODAL_COPY.likelyVehicle(lead.name)
              : DECISION_MODAL_COPY.likely(lead.name, lead.shortName)}
          </p>
          <p className="text-muted-foreground">{DECISION_MODAL_COPY.likelyWhy}</p>
          <div className="flex flex-wrap items-center gap-3">
            <Button
              type="primary"
              loading={saving}
              data-testid="decision-modal-use-likely"
              onClick={() => {
                onSave(lead.body);
              }}
            >
              {DECISION_MODAL_COPY.useLikely}
            </Button>
            {mayCreatePostType ? (
              <Link
                to={createPostTypeHref(setKey, row.code)}
                className="text-link"
                data-testid="decision-modal-create-instead"
              >
                {DECISION_MODAL_COPY.createInstead}
              </Link>
            ) : null}
            {other}
          </div>
        </div>
      ) : null}

      {lead.kind === 'CREATE' ? (
        <div className="flex flex-col gap-2" data-testid="decision-modal-create">
          <p className="font-medium text-heading">{DECISION_MODAL_COPY.noLikely(row.label)}</p>
          <p className="text-muted-foreground">{DECISION_MODAL_COPY.createBody}</p>
          <div className="flex flex-wrap items-center gap-3">
            <Link
              to={createPostTypeHref(setKey, row.code)}
              className="text-link"
              data-testid="decision-modal-create-link"
            >
              {DECISION_MODAL_COPY.create}
            </Link>
            {other}
          </div>
        </div>
      ) : null}

      {lead.kind === 'FORM' ? (
        <DecisionForm
          row={row}
          setKey={setKey}
          days={days}
          postTypes={masters.postTypes}
          vehicleTypes={masters.vehicleTypes}
          policies={masters.policies}
          unallocatableUnitIds={masters.unallocatable}
          mayCreatePostType={mayCreatePostType}
          saving={saving}
          onSave={onSave}
          onCancel={onClose}
        />
      ) : null}
    </>
  );
};

export const DecisionModal = ({
  open,
  onClose,
  setKey,
  target,
  days,
  mayCreatePostType,
}: {
  open: boolean;
  onClose: () => void;
  setKey: string;
  /** The decision to preview — kept by the panel after closing, so the closing dialog never goes blank. */
  target: DecisionModalTarget | null;
  days: readonly TemplateDayDto[];
  mayCreatePostType: boolean;
}): React.ReactElement => {
  const decisionClass = target?.class ?? 'SOURCE';
  const reading = open && target !== null;
  const decisions = useTemplateDecisions(setKey, decisionClass, { enabled: reading });
  const masters = useDecisionMasters(
    {
      orgUnits: reading && decisionClass === 'SOURCE',
      postTypes: reading && decisionClass === 'FORCE_COLUMN',
      vehicleTypes: reading && decisionClass === 'FORCE_COLUMN',
      policies: reading && decisionClass === 'POLICY',
    },
    days,
  );
  const save = useSaveDecision(setKey, onClose);
  const row = decisions.data?.items.find((each) => each.code === target?.code);

  return (
    <Modal open={open} title={target?.words} footer={null} width={640} centered destroyOnHidden onCancel={onClose}>
      <div className="flex max-h-[65vh] flex-col gap-3 overflow-y-auto pr-1 text-sm" data-testid="decision-modal">
        {target === null ? null : decisions.isError ? (
          /* A failed read is not a decision that has gone — say what failed, and offer the page. */
          <div className="flex flex-col gap-2" data-testid="decision-modal-failed">
            <p>{decisions.error.message}</p>
            <Link to={templateDecisionHref(setKey, target.class, target.code)} className="text-link">
              {DECISION_MODAL_COPY.openPage}
            </Link>
          </div>
        ) : decisions.isPending || masters.pending ? (
          <div className="flex justify-center py-8">
            <Spin />
          </div>
        ) : (
          <ModalBody
            setKey={setKey}
            target={target}
            row={row}
            masters={masters}
            days={days}
            mayCreatePostType={mayCreatePostType}
            saving={save.isPending}
            onSave={(body) => {
              save.mutate(body);
            }}
            onClose={onClose}
          />
        )}
      </div>
    </Modal>
  );
};
